import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../AppContext";
import { getCurrentUserRole } from "../../../common/infrastructure/helpers/userHelper";
import { useFeatureFlag } from "../../../common/infrastructure/hooks/useFeatureFlag";
import { usePushNotifications } from "../../../common/infrastructure/hooks/usePushNotifications";
import {
  getNotificationPermission,
  getReservationNotificationDialogDecision,
  getStaffNotificationDialogDecision,
  isInAppBrowser,
  isIosSafariTab,
  isPushSupported,
  isStaffRole,
} from "../../../common/infrastructure/utilities/pushUtility";
import {
  clearNotificationPromptHistory,
  NOTIFICATION_PROMPT_STORAGE_KEY,
  readNotificationPromptHistory,
  readNotificationPromptLastShownAt,
  recordNotificationPromptDismissal,
  recordNotificationPromptShown,
  STAFF_NOTIFICATION_PROMPT_STORAGE_KEY,
} from "../../../common/infrastructure/utilities/notificationPromptStorageUtility";
import { getErrorToast } from "../../../common/infrastructure/utilities/toastUtility";
import {
  FeatureFlagKey,
  NotificationDialogAudience,
  NotificationDialogDecision,
  NotificationDialogView,
  PushSubscribeResult,
} from "../../../common/enums";

/** Wait for the reservation-success animation (or the platform to settle) before inviting. */
export const NOTIFICATION_DIALOG_DELAY_MS = 1500;

/** How long the "Готово!" confirmation stays before the dialog closes itself. */
export const NOTIFICATION_DIALOG_DONE_CLOSE_MS = 1500;

interface IUseNotificationPermissionDialogParams {
  /** Clients on the reservation-success screens (default), or staff on opening the platform. */
  audience?: NotificationDialogAudience;
  /** Client audience: only confirmed reservations get the invitation (not the waiting list, not unpaid ones). */
  isConfirmedReservation?: boolean;
  /** Shown as the title of the sample notification. */
  studioName?: string | null;
  /** Date and start time of the reservation, used in the sample notification. */
  eventDate?: Date | string | null;
  startTime?: string | null;
}

const PREVIEW_FALLBACK_BODY = "Напомняне за предстоящата ви резервация.";

const CLIENT_SUBTITLE = "Искате да получавате известия от нас?";
// General on purpose: notifications for new reservations are a separate switch in "Моят профил",
// so the dialog shows the way there instead of promising them.
const STAFF_SUBTITLE = "Искате ли да получавате известия от Planny на това устройство?";

const padTwo = (value: number) => String(value).padStart(2, "0");

/** Mirrors the default reminder wording: "Ще ви очакваме на dd.MM от HH:mmч". */
const buildPreviewBody = (eventDate?: Date | string | null, startTime?: string | null) => {
  if (!eventDate || !startTime) {
    return PREVIEW_FALLBACK_BODY;
  }
  const date = eventDate instanceof Date ? eventDate : new Date(eventDate);
  if (Number.isNaN(date.getTime())) {
    return PREVIEW_FALLBACK_BODY;
  }
  return `Ще ви очакваме на ${padTwo(date.getDate())}.${padTwo(date.getMonth() + 1)} от ${startTime}ч`;
};

/**
 * Drives the notification permission dialog: on the reservation-success screens for clients,
 * and in the layout for staff opening the platform. Decides whether to show it (feature flag,
 * role, device, previous answers), opens it after a short delay, and handles the answer.
 */
export const useNotificationPermissionDialog = ({
  audience = NotificationDialogAudience.Client,
  isConfirmedReservation = false,
  studioName,
  eventDate,
  startTime,
}: IUseNotificationPermissionDialogParams) => {
  const flagEnabled = useFeatureFlag(FeatureFlagKey.EnableAppNotifications, false);
  const { subscribe, hasActiveSubscription } = usePushNotifications();
  const { isAuthenticated, currentUser } = useAppContext();

  const isStaffAudience = audience === NotificationDialogAudience.Staff;
  const isStaff = isAuthenticated && !!currentUser && isStaffRole(getCurrentUserRole(currentUser));
  // A global admin inside someone's profile (opened from the admin panel) is never invited:
  // saying yes would send that person's notifications to the admin's device.
  const isImpersonating = !!currentUser?.impersonatedBy;
  const storageKey = isStaffAudience
    ? STAFF_NOTIFICATION_PROMPT_STORAGE_KEY
    : NOTIFICATION_PROMPT_STORAGE_KEY;

  const [isVisible, setIsVisible] = useState(false);
  const [view, setView] = useState<NotificationDialogView>(NotificationDialogView.Invite);
  const [isBusy, setIsBusy] = useState(false);

  const isIosFlowRef = useRef(false);
  const hasOpenedRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (hasOpenedRef.current || isImpersonating) {
      return undefined;
    }

    const device = {
      flagEnabled,
      supported: isPushSupported(),
      permission: getNotificationPermission(),
      iosSafariTab: isIosSafariTab(),
      inAppBrowser: isInAppBrowser(),
      history: readNotificationPromptHistory(storageKey),
      lastShownAt: readNotificationPromptLastShownAt(),
      now: new Date(),
    };
    const decision = isStaffAudience
      ? getStaffNotificationDialogDecision({ ...device, isStaff })
      : getReservationNotificationDialogDecision({ ...device, isConfirmedReservation });

    if (decision === NotificationDialogDecision.None) {
      return undefined;
    }

    let isCancelled = false;
    const openTimer = setTimeout(async () => {
      // A push subscription on this device means notifications are already on here,
      // whatever the permission reports.
      if ((await hasActiveSubscription()) || isCancelled) {
        return;
      }
      // Another invitation opened on this device while this one waited (the staff one and a
      // reservation one at the same time): only one may show.
      if (readNotificationPromptLastShownAt() !== device.lastShownAt) {
        return;
      }
      hasOpenedRef.current = true;
      isIosFlowRef.current = decision === NotificationDialogDecision.IosInvite;
      recordNotificationPromptShown(new Date());
      setView(NotificationDialogView.Invite);
      setIsVisible(true);
    }, NOTIFICATION_DIALOG_DELAY_MS);

    return () => {
      isCancelled = true;
      clearTimeout(openTimer);
    };
  }, [
    flagEnabled,
    isConfirmedReservation,
    isStaffAudience,
    isStaff,
    isImpersonating,
    storageKey,
    hasActiveSubscription,
  ]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    },
    []
  );

  const close = useCallback(() => {
    setIsVisible(false);
  }, []);

  const onDismiss = useCallback(() => {
    // "Не сега" on the invitation, or "Разбрах" on the iPhone steps.
    recordNotificationPromptDismissal(new Date(), storageKey);
    close();
  }, [close, storageKey]);

  const onEnable = useCallback(async () => {
    if (isIosFlowRef.current) {
      // Push is unavailable in a Safari tab — show how to add Planny to the Home Screen.
      setView(NotificationDialogView.IosSteps);
      return;
    }

    setIsBusy(true);
    const result = await subscribe();
    setIsBusy(false);

    switch (result) {
      case PushSubscribeResult.Subscribed:
        clearNotificationPromptHistory(storageKey);
        setView(NotificationDialogView.Done);
        closeTimerRef.current = setTimeout(close, NOTIFICATION_DIALOG_DONE_CLOSE_MS);
        break;
      case PushSubscribeResult.Dismissed:
        // The system prompt was closed without a choice — same as "Не сега".
        recordNotificationPromptDismissal(new Date(), storageKey);
        close();
        break;
      case PushSubscribeResult.Denied:
        // The browser remembers a block; asking again would be refused anyway.
        close();
        break;
      default:
        getErrorToast(
          "Известията не успяха да се включат. Опитайте отново от „Моят профил“ → „Известия“."
        );
        close();
        break;
    }
  }, [subscribe, close, storageKey]);

  const previewTitle = studioName?.trim() || "Planny";
  const previewBody = useMemo(() => buildPreviewBody(eventDate, startTime), [eventDate, startTime]);

  const subtitle = isStaffAudience ? STAFF_SUBTITLE : CLIENT_SUBTITLE;
  // No sample for staff: we do not promise any specific kind of notification.
  const showPreview = !isStaffAudience;
  // Staff turn on new-reservation notifications themselves, in "Моят профил" → "Известия".
  const showNewReservationsSteps = isStaffAudience;

  return {
    isVisible,
    view,
    isBusy,
    onEnable,
    onDismiss,
    subtitle,
    showPreview,
    showNewReservationsSteps,
    previewTitle,
    previewBody,
  };
};
